/**
 * Quote discount application.
 *
 * A discount is attached to a booking quote (e.g. by a GoHighLevel rep via the
 * apply-quote-discount webhook) and applied at checkout. It is computed off the
 * grand total (fare + gratuity + taxes), mirroring the Return Trip Discount in
 * `apps/viewer/src/app/api/booking/calculate-price/route.ts`:
 * a negative `type: 'discount'` line item is added AND `total` is reduced
 * separately, because `total` is not the sum of `breakdown`.
 *
 * This module is pure and immutable — it never mutates the input pricing.
 */

export type QuoteDiscountType = 'percent' | 'flat'

export interface QuoteDiscount {
  type: QuoteDiscountType
  /** Raw value: percent (e.g. 10 = 10%) or flat dollars (e.g. 50 = $50 off). */
  value: number
}

/**
 * Minimal shape the discount needs. Works with the full shared `PricingResult`,
 * the viewer's `PricingResult`, and the partial `effectivePricing` literal in
 * create-payment-intent — the caller's exact type is preserved on the way out.
 */
export interface DiscountablePricing {
  total: number
  breakdown: Array<{ label: string; amount: number; type: string }>
}

export type ApplyDiscountResult<T extends DiscountablePricing> =
  | { ok: true; pricing: T; discountAmount: number; label: string }
  /** `exceeds_total` = the discount is >= the price and cannot be charged (reject). */
  /** `invalid` = no usable discount (zero/negative/NaN); caller should ignore it. */
  | { ok: false; reason: 'exceeds_total' | 'invalid' }

const roundCents = (n: number): number => Math.round(n * 100) / 100

/** Clamp a percent into the 0–100 range. */
const clampPercent = (pct: number): number => Math.min(100, Math.max(0, pct))

/**
 * Marker written into a discount line item's `metadata` when an operator sets the
 * discount from the Builder, so the line can be found again without guessing from
 * its label.
 */
export const OPERATOR_DISCOUNT_SOURCE = 'operator_discount'

/** The shape of a `pricing_breakdown` line, as far as discount handling cares. */
export interface DiscountLineItem {
  label?: unknown
  type?: unknown
  amount?: unknown
  metadata?: unknown
}

/**
 * Whether a pricing_breakdown line is THE operator/quote discount — the one the
 * Apply Discount dialog owns and may replace.
 *
 * `type: 'discount'` alone is not enough: the round-trip feature emits
 * `Return Trip Discount (15%)` with the same type, and overwriting that would
 * silently undo a pricing feature. Lines written by the Builder carry an explicit
 * metadata marker; older ones are matched on the exact label `applyQuoteDiscount`
 * produces (`Discount` or `Discount (10%)`).
 *
 * A negative `type: 'adjustment'` line (e.g. `Summer Discount (-20%)`) is a
 * pricing RULE and is never a discount for these purposes.
 */
export function isOperatorDiscountLine(line: DiscountLineItem | null | undefined): boolean {
  if (!line || typeof line !== 'object' || line.type !== 'discount') return false

  const metadata = line.metadata as { source?: unknown } | null | undefined
  if (metadata?.source === OPERATOR_DISCOUNT_SOURCE) return true

  return /^Discount(?: \(|$)/.test(String(line.label ?? ''))
}

/**
 * Coerce one operator/webhook-supplied amount to a positive number.
 *
 * Inputs arrive as numbers, numeric strings, empty strings, or decorated values
 * like "$50" / "10%" (GHL field mappings and free-text form inputs both do this).
 * Anything unparseable or <= 0 means "not set".
 */
function coerceAmount(raw: unknown): number {
  if (typeof raw === 'number') return Number.isFinite(raw) && raw > 0 ? raw : 0
  if (typeof raw === 'string') {
    const cleaned = raw.replace(/[^0-9.]/g, '')
    if (!cleaned) return 0
    const n = Number.parseFloat(cleaned)
    return Number.isFinite(n) && n > 0 ? n : 0
  }
  return 0
}

/**
 * Normalize a percent/flat pair into the single active discount that gets stored.
 *
 * These are the product rules the GHL webhook established, kept here so the
 * Builder's Apply Discount dialog can't drift from them:
 *  - **Flat wins** when both are supplied.
 *  - Percent is clamped to 0-100.
 *  - Both empty/zero -> `null`, meaning "clear any existing discount".
 *
 * The Deno edge function `supabase/functions/apply-quote-discount/index.ts` keeps
 * its own copy of this logic (it can't import this workspace package) — change
 * both together.
 */
export function normalizeDiscountInput(input: {
  percent?: unknown
  flat?: unknown
}): QuoteDiscount | null {
  const flat = coerceAmount(input.flat)
  if (flat > 0) return { type: 'flat', value: flat }

  const percent = coerceAmount(input.percent)
  if (percent > 0) return { type: 'percent', value: clampPercent(percent) }

  return null
}

/**
 * Compute the dollar discount amount off a grand total. Returns 0 for any
 * non-positive / non-finite value.
 */
export function computeDiscountAmount(total: number, discount: QuoteDiscount): number {
  if (!Number.isFinite(discount.value) || discount.value <= 0) return 0
  if (discount.type === 'percent') {
    return roundCents(total * (clampPercent(discount.value) / 100))
  }
  return roundCents(discount.value)
}

/** Human-readable line-item label for a discount. */
export function discountLabel(discount: QuoteDiscount): string {
  if (discount.type === 'percent') {
    // Trim trailing zeros: 10 -> "10", 12.5 -> "12.5"
    const pct = Number(clampPercent(discount.value).toFixed(2))
    return `Discount (${pct}%)`
  }
  return 'Discount'
}

/**
 * Apply a discount to a pricing result, returning a NEW pricing object.
 *
 * - `{ ok: true }`  — discounted pricing (negative line item + reduced total).
 * - `{ ok: false, reason: 'exceeds_total' }` — discount >= total; must be rejected.
 * - `{ ok: false, reason: 'invalid' }` — no usable discount; caller ignores it.
 */
export function applyQuoteDiscount<T extends DiscountablePricing>(
  pricing: T,
  discount: QuoteDiscount
): ApplyDiscountResult<T> {
  const discountAmount = computeDiscountAmount(pricing.total, discount)

  if (discountAmount <= 0) {
    return { ok: false, reason: 'invalid' }
  }

  // A discount that meets or exceeds the total would drive the charge to $0 or
  // below — reject rather than charge a nonsense amount.
  if (discountAmount >= pricing.total) {
    return { ok: false, reason: 'exceeds_total' }
  }

  const label = discountLabel(discount)
  const lineItem = {
    label,
    amount: -discountAmount,
    type: 'discount' as const,
  }

  // Preserve the caller's exact type; only `breakdown` and `total` change.
  const pricingWithDiscount = {
    ...pricing,
    breakdown: [...pricing.breakdown, lineItem],
    total: roundCents(pricing.total - discountAmount),
  } as T

  return { ok: true, pricing: pricingWithDiscount, discountAmount, label }
}
